import React, { createContext, useContext, useState, useEffect } from 'react';

const ADMIN_API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/secure-admin`;

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${ADMIN_API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setAdmin(data.admin);
        } else {
          localStorage.removeItem('admin_token');
          setToken(null);
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        localStorage.removeItem('admin_token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const login = async (email, password, totpCode = null, recoveryCode = null) => {
    const response = await fetch(`${ADMIN_API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        totp_code: totpCode,
        recovery_code: recoveryCode
      })
    });

    if (!response.ok) {
      // Check if 2FA is required
      const is2FARequired = response.headers.get('X-2FA-Required') === 'true';
      
      // Clone response before reading to avoid "body stream already read" error
      let data;
      try {
        data = await response.clone().json();
      } catch (e) {
        data = { detail: 'Login failed' };
      }
      
      if (is2FARequired || data.detail === '2FA code required') {
        return { requires2FA: true };
      }
      throw new Error(data.detail || 'Login failed');
    }

    const data = await response.json();

    localStorage.setItem('admin_token', data.access_token);
    setToken(data.access_token);
    setAdmin(data.admin);
    setRequires2FASetup(data.force_2fa_setup);
    setRequiresPasswordReset(data.force_password_reset);

    return {
      success: true,
      force2FASetup: data.force_2fa_setup,
      forcePasswordReset: data.force_password_reset
    };
  };

  const logout = async () => {
    if (token) {
      try {
        await fetch(`${ADMIN_API_URL}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    localStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
    setRequires2FASetup(false);
    setRequiresPasswordReset(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await fetch(`${ADMIN_API_URL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Password change failed');
    }

    setRequiresPasswordReset(false);
    return { success: true };
  };

  const setup2FA = async () => {
    const response = await fetch(`${ADMIN_API_URL}/auth/setup-2fa`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Failed to initialize 2FA setup');
    }

    return await response.json();
  };

  const confirm2FA = async (totpCode) => {
    const response = await fetch(`${ADMIN_API_URL}/auth/confirm-2fa?totp_code=${totpCode}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Failed to confirm 2FA');
    }

    setRequires2FASetup(false);
    return await response.json();
  };

  const value = {
    admin,
    token,
    loading,
    requires2FASetup,
    requiresPasswordReset,
    login,
    logout,
    changePassword,
    setup2FA,
    confirm2FA,
    isAuthenticated: !!admin
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthProvider;
